import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Badge, Button, ColorField, FileUpload, IconButton, ImagePreview, Input, LivePreview, Modal, PageHeader, Select, SettingCard, Spin, Switch, Textarea, useAlert } from '../ui';
import type { UploadedFile } from '../ui';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Calendar, CheckCircle2, Hash, Info, Megaphone, Mic, Monitor, Pencil, RotateCcw, Save, Trash2, Volume2 } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import speech from '../Fonctions/speech';

/** POST générique vers api/GestionFile/ManageParametres — retourne res.Value en promesse. */
function postParametres(authHeader: () => string, payload: any): Promise<any> {
    const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + datas.API_LINKs.GestionFile.ManageParametres,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader(),
        },
        data: Crypter.encrypt(JSON.stringify(payload))
    };

    return axios.request(config)
        .then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value);
}

/**
 * GET api/GestionFile/ManageParametres?CipherParam= — lecture seule.
 * L'Option est forcée à LISTER côté serveur : ne peut jamais muter.
 */
function getParametres(authHeader: () => string, domaine: string, codeAbonne: string): Promise<any> {
    const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + datas.API_LINKs.GestionFile.ManageParametresGet
            + Crypter.encrypt(JSON.stringify({ Domaine: domaine, CodeAbonne: codeAbonne })),
        headers: {
            'Accept': 'text/plain',
            'Authorization': authHeader(),
        }
    };

    return axios.request(config)
        .then((response: any) => JSON.parse(Crypter.decrypt(response.data)).Value);
}

/** Payload de base pour un domaine donné (ParametrageRequest : Domaine/Option/CodeAbonne). */
function basePayload(domaine: string, option: string, codeAbonne: string) {
    return {
        Domaine: domaine,
        Option: option,
        CodeAbonne: codeAbonne,
    };
}

/** Formate une date/heure en fr-FR, tolérant les valeurs invalides. */
function formatDateFr(v: any): string {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return String(v)
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
}

/** Formate une date en AAAAMMJJ pour l'aperçu du code de demande. */
function formatDateApercu(d: Date) {
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

export default function GestionParametres() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))

    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])

    // Droit D07 « Parametres Tenant » (liste des droits reçue à la connexion)
    const habilitations: any[] = (() => {
        try { return JSON.parse(Crypter.decrypt(localStorage.getItem(datas.Habilitations) || '')) || [] } catch { return [] }
    })()
    const canEdit = ['SUADMIN', 'ADMIN', 'IT'].includes(user.RoleId) || habilitations.some((h: any) => h.CodeHabi === 'D07')

    useEffect(() => {
        LoadFiliales()
        LoadServices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setFiliales(res.Result || [])
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function LoadServices() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    // Result est un tableau PLAT [{CodeS, Libelle, CodeFiliale, ...}]
                    // — tolérer aussi la forme [filiales, services]
                    const svc = Array.isArray(res.Result?.[1]) ? res.Result[1] : (res.Result || [])
                    setServices(svc)
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    return (
        <div>
            <PageHeader
                title="Paramètres tenant"
                subtitle="Fiche globale de configuration — check-in, formats, annonce vocale, réinitialisation"
            />

            {/* Règle de portée + droit requis */}
            <div className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-primary-soft)]/40 text-sm text-[var(--eb-text)]">
                <Info size={16} className="mt-0.5 flex-shrink-0 text-[var(--eb-primary)]" />
                <span>
                    Chaque paramètre peut être défini par <b>entité</b>, <b>agence</b> ou <b>service</b> — la règle la plus
                    précise gagne (service+agence &gt; agence &gt; service &gt; entité &gt; défaut).
                    {!canEdit && <b className="block mt-1 text-amber-600">Lecture seule — le droit D07 « Paramètres Tenant » est requis pour modifier.</b>}
                </span>
            </div>

            {/* Fiche globale : toutes les sections en cartes */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                <SectionCheckin authHeader={authHeader} codeAbonne={user.CodeAbonne} filiales={filiales} canEdit={canEdit} />
                <SectionNumeroTicket authHeader={authHeader} codeAbonne={user.CodeAbonne} filiales={filiales} services={services} canEdit={canEdit} />
                <SectionCodeDemande authHeader={authHeader} codeAbonne={user.CodeAbonne} canEdit={canEdit} />
                <SectionVoixAppel authHeader={authHeader} codeAbonne={user.CodeAbonne} filiales={filiales} canEdit={canEdit} />
                <SectionResetTickets authHeader={authHeader} codeAbonne={user.CodeAbonne} filiales={filiales} canEdit={canEdit} />
                <SectionModeAnnonce authHeader={authHeader} codeAbonne={user.CodeAbonne} filiales={filiales} services={services} canEdit={canEdit} />
                <div className="xl:col-span-2">
                    <SectionEcranLive authHeader={authHeader} user={user} canEdit={canEdit} />
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Domaine CHECKIN — check-in à l'arrivée par filiale / global       */
/* ------------------------------------------------------------------ */
function SectionCheckin(props: { authHeader: () => string; codeAbonne: string; filiales: any[]; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [regles, setRegles] = useState<any[]>([])

    const [codeFiliale, setCodeFiliale] = useState<string>('')
    const [checkinRequis, setCheckinRequis] = useState<boolean>(false)
    const [regleEdit, setRegleEdit] = useState<any>(null)

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        setLoading(true)
        getParametres(props.authHeader, 'CHECKIN', props.codeAbonne)
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    setRegles(res.Result || [])
                }
                else {
                    setRegles([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setLoading(false));
    }

    function handleEdit(regle: any) {
        setRegleEdit(regle)
        setCodeFiliale(regle.CodeFiliale || '')
        setCheckinRequis(!!regle.CheckinRequis)
    }

    function handleDelete(regle: any) {
        setSaving(true)
        // DELETE par scope : CodeFiliale null = règle globale
        postParametres(props.authHeader, {
            ...basePayload('CHECKIN', 'DELETE', props.codeAbonne),
            CodeFiliale: regle.CodeFiliale ?? null,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    // Result = état courant après mutation — pas besoin de re-lister
                    setRegles(res.Result || [])
                    if (regleEdit?.CodeFiliale === regle.CodeFiliale) setRegleEdit(null)
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setSaving(false));
    }

    function handleSave() {
        setSaving(true)
        // UPSERT — unicité par (CodeAbonne, CodeFiliale) ; CheckinRequis requis
        postParametres(props.authHeader, {
            ...basePayload('CHECKIN', 'UPSERT', props.codeAbonne),
            CodeFiliale: codeFiliale || null,
            CheckinRequis: checkinRequis,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    setRegleEdit(null)
                    setRegles(res.Result || [])
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setSaving(false));
    }

    return (
        <SettingCard title="Check-in à l'arrivée" icon={<CheckCircle2 size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-3 leading-relaxed">
                    Si actif, les tickets en ligne passent en statut 0 et doivent être confirmés
                    à l'arrivée (QR ou saisie).
                </p>

                {/* Liste des règles existantes */}
                <div className="mb-3 overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Filiale</th>
                                <th className="px-3 py-2 font-semibold">Check-in</th>
                                <th className="px-3 py-2 font-semibold">Dernière maj</th>
                                {props.canEdit && <th className="px-3 py-2 font-semibold w-24">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {regles.length === 0 && (
                                <tr>
                                    <td colSpan={props.canEdit ? 4 : 3} className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">
                                        Aucune règle configurée
                                    </td>
                                </tr>
                            )}
                            {regles.map((r: any, i: number) => (
                                <tr key={r.Id ?? i} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2">{r.CodeFiliale || 'Règle globale'}</td>
                                    <td className="px-3 py-2">
                                        <Badge color={r.CheckinRequis ? 'green' : 'gray'}>
                                            {r.CheckinRequis ? 'Requis' : 'Non requis'}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-[var(--eb-text-muted)]">{formatDateFr(r.DateMaj)}</td>
                                    {props.canEdit && (
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={28} onClick={() => handleEdit(r)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton tooltip="Supprimer" color="danger" size={28} onClick={() => handleDelete(r)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Formulaire d'ajout / modification */}
                {props.canEdit && (
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[220px] flex-1">
                            <Select
                                label="Filiale"
                                value={codeFiliale}
                                onChange={setCodeFiliale}
                                options={[
                                    { value: '', label: 'Règle globale' },
                                    ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                ]}
                            />
                        </div>
                        <div className="flex items-center gap-2 pb-1">
                            <Switch checked={checkinRequis} onChange={setCheckinRequis} />
                            <span className="text-sm text-[var(--eb-text)]">Check-in requis</span>
                        </div>
                        <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={16} />} onClick={handleSave}>
                            Enregistrer
                        </Button>
                    </div>
                )}
            </Spin>
        </SettingCard>
    );
}

/* ------------------------------------------------------------------ */
/*  Domaine NUMERO_TICKET — format du numéro de ticket (multi-règles) */
/*  Portée : (CodeAbonne, CodeFiliale, CodeS) — la plus spécifique    */
/*  gagne : service+agence > agence > service > défaut                */
/* ------------------------------------------------------------------ */
function SectionNumeroTicket(props: { authHeader: () => string; codeAbonne: string; filiales: any[]; services: any[]; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [regles, setRegles] = useState<any[]>([])
    const [regleEdit, setRegleEdit] = useState<any>(null)

    const [codeFiliale, setCodeFiliale] = useState<string>('')
    const [codeS, setCodeS] = useState<string>('')
    const [prefixe, setPrefixe] = useState<string>('T')
    const [inclureCodeS, setInclureCodeS] = useState<boolean>(false)
    const [longueurSeq, setLongueurSeq] = useState<number>(3)

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        setLoading(true)
        getParametres(props.authHeader, 'NUMERO_TICKET', props.codeAbonne)
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    setRegles(res.Result || [])
                }
                else {
                    setRegles([])
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setLoading(false));
    }

    function handleEdit(regle: any) {
        setRegleEdit(regle)
        setCodeFiliale(regle.CodeFiliale ?? '')
        setCodeS(regle.CodeS ?? '')
        setPrefixe(regle.Prefixe ?? 'T')
        setInclureCodeS(!!regle.InclureCodeS)
        setLongueurSeq(regle.LongueurSeq ?? 3)
    }

    function handleDelete(regle: any) {
        setSaving(true)
        // DELETE par scope (CodeFiliale, CodeS) — null = règle globale / tous services
        postParametres(props.authHeader, {
            ...basePayload('NUMERO_TICKET', 'DELETE', props.codeAbonne),
            CodeFiliale: regle.CodeFiliale ?? null,
            CodeS: regle.CodeS ?? null,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    setRegles(res.Result || [])
                    setRegleEdit(null)
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setSaving(false));
    }

    function handleSave() {
        const seq = Number(longueurSeq) || 0
        if (seq < 1 || seq > 10) {
            alert.warning('La longueur de la séquence doit être entre 1 et 10.')
            return
        }
        setSaving(true)
        // UPSERT — unicité par (CodeAbonne, CodeFiliale, CodeS)
        postParametres(props.authHeader, {
            ...basePayload('NUMERO_TICKET', 'UPSERT', props.codeAbonne),
            CodeFiliale: codeFiliale || null,
            CodeS: codeS || null,
            Prefixe: prefixe,
            InclureCodeS: inclureCodeS,
            LongueurSeq: seq,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    setRegleEdit(null)
                    setRegles(res.Result || [])
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setSaving(false));
    }

    // Services filtrés par la filiale choisie (si une filiale est sélectionnée)
    const servicesFiltres = props.services.filter((s: any) => !codeFiliale || s.CodeFiliale == codeFiliale)

    const apercu = `${inclureCodeS && codeS ? codeS + '-' : ''}${prefixe}${String(12).padStart(Math.max(0, Number(longueurSeq) || 0), '0')}`;

    return (
        <SettingCard title="Numéro de ticket" icon={<Hash size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-3 leading-relaxed">
                    Format du numéro : préfixe + séquence de 1 à 10 chiffres (compteur
                    par file, reset quotidien). Portée par règle : globale, filiale
                    ou service — la plus spécifique gagne.
                </p>

                {/* Règles existantes */}
                <div className="mb-3 overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Filiale</th>
                                <th className="px-3 py-2 font-semibold">Service</th>
                                <th className="px-3 py-2 font-semibold">Format</th>
                                <th className="px-3 py-2 font-semibold">Statut</th>
                                {props.canEdit && <th className="px-3 py-2 font-semibold w-20">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {regles.length === 0 && (
                                <tr>
                                    <td colSpan={props.canEdit ? 5 : 4} className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">
                                        Aucune règle configurée
                                    </td>
                                </tr>
                            )}
                            {regles.map((r: any, i: number) => (
                                <tr key={r.Id ?? i} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2">{r.CodeFiliale || <span className="text-[var(--eb-text-muted)]">Globale</span>}</td>
                                    <td className="px-3 py-2">{r.CodeS || <span className="text-[var(--eb-text-muted)]">Tous</span>}</td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {r.InclureCodeS ? 'CodeS-' : ''}{r.Prefixe}{'0'.repeat(Math.max(0, r.LongueurSeq || 0)) || '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge color={r.IsActif !== false ? 'green' : 'gray'}>
                                            {r.IsActif !== false ? 'Actif' : 'Inactif'}
                                        </Badge>
                                    </td>
                                    {props.canEdit && (
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={28} onClick={() => handleEdit(r)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton tooltip="Supprimer" color="danger" size={28} onClick={() => handleDelete(r)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {props.canEdit && regleEdit && (
                    <p className="text-xs text-[var(--eb-primary)] mb-2">
                        Modification de la règle : {regleEdit.CodeFiliale || 'globale'} / {regleEdit.CodeS || 'tous services'}
                        <button className="ml-2 underline" onClick={() => setRegleEdit(null)}>annuler</button>
                    </p>
                )}

                {/* Formulaire d'ajout / modification */}
                {props.canEdit && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <Select
                                label="Filiale"
                                value={codeFiliale}
                                onChange={(v: string) => { setCodeFiliale(v); setCodeS(''); }}
                                options={[
                                    { value: '', label: 'Règle globale' },
                                    ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                ]}
                            />
                            <Select
                                label="Service"
                                value={codeS}
                                onChange={setCodeS}
                                options={[
                                    { value: '', label: 'Tous les services' },
                                    ...servicesFiltres.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))
                                ]}
                            />
                            <Input
                                label="Préfixe"
                                type="text"
                                value={prefixe}
                                onChange={(e: any) => setPrefixe(e.target.value)}
                                placeholder="Ex : T"
                            />
                            <Input
                                label="Longueur de la séquence (1-10)"
                                type="number"
                                min={1}
                                max={10}
                                value={longueurSeq}
                                onChange={(e: any) => setLongueurSeq(e.target.value)}
                                placeholder="Ex : 3"
                            />
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <Switch checked={inclureCodeS} onChange={setInclureCodeS} />
                            <span className="text-sm text-[var(--eb-text)]">Inclure le code service dans le numéro</span>
                        </div>

                        <div className="mb-3 rounded-lg border border-dashed border-[var(--eb-border)] bg-[var(--eb-bg)] px-3 py-2">
                            <span className="text-xs text-[var(--eb-text-muted)]">Aperçu : </span>
                            <span className="text-sm font-bold text-[var(--eb-text)]">{apercu}</span>
                        </div>

                        <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={16} />} onClick={handleSave}>
                            {regleEdit ? 'Mettre à jour' : 'Enregistrer'}
                        </Button>
                    </>
                )}
            </Spin>
        </SettingCard>
    );
}

/* ------------------------------------------------------------------ */
/*  Domaine CODE_DEMANDE — format du code de demande / RDV            */
/* ------------------------------------------------------------------ */
function SectionCodeDemande(props: { authHeader: () => string; codeAbonne: string; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [cfg, setCfg] = useState<any>(null)
    const [prefixe, setPrefixe] = useState<string>('RDV-')
    const [inclureDate, setInclureDate] = useState<boolean>(true)
    const [longueurSeq, setLongueurSeq] = useState<number>(5)

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        setLoading(true)
        getParametres(props.authHeader, 'CODE_DEMANDE', props.codeAbonne)
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    const cfg = (res.Result || [])[0]
                    setCfg(cfg || null)
                    if (cfg) {
                        setPrefixe(cfg.Prefixe ?? 'RDV-')
                        setInclureDate(!!cfg.InclureDate)
                        setLongueurSeq(cfg.LongueurSeq ?? 5)
                    }
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setLoading(false));
    }

    function handleSave() {
        const seq = Number(longueurSeq) || 0
        if (seq < 1 || seq > 10) {
            alert.warning('La longueur de la séquence doit être entre 1 et 10.')
            return
        }
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('CODE_DEMANDE', 'UPSERT', props.codeAbonne),
            Prefixe: prefixe,
            InclureDate: inclureDate,
            LongueurSeq: seq,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    // Result = état courant après mutation (compteur conservé)
                    const updated = (res.Result || [])[0]
                    if (updated) setCfg(updated)
                }
                else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setSaving(false));
    }

    const apercu = `${prefixe}${inclureDate ? formatDateApercu(new Date()) + '-' : ''}${String(5).padStart(Math.max(0, Number(longueurSeq) || 0), '0')}`;

    return (
        <SettingCard title="Code de demande" icon={<Calendar size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-3 leading-relaxed">
                    Format du code de demande / rendez-vous : préfixe, date optionnelle
                    et séquence de N chiffres.
                </p>

                {props.canEdit && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <Input
                                label="Préfixe"
                                type="text"
                                value={prefixe}
                                onChange={(e: any) => setPrefixe(e.target.value)}
                                placeholder="Ex : RDV-"
                            />
                            <Input
                                label="Longueur de la séquence (1-10)"
                                type="number"
                                min={1}
                                max={10}
                                value={longueurSeq}
                                onChange={(e: any) => setLongueurSeq(e.target.value)}
                                placeholder="Ex : 5"
                            />
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <Switch checked={inclureDate} onChange={setInclureDate} />
                            <span className="text-sm text-[var(--eb-text)]">Inclure la date dans le code</span>
                        </div>
                    </>
                )}

                <div className="mb-3 rounded-lg border border-dashed border-[var(--eb-border)] bg-[var(--eb-bg)] px-3 py-2">
                    <span className="text-xs text-[var(--eb-text-muted)]">Aperçu : </span>
                    <span className="text-sm font-bold text-[var(--eb-text)]">{apercu}</span>
                </div>

                {cfg && (cfg.DerniereDate || cfg.DernierNumero != null || cfg.DateMaj) && (
                    <p className="text-xs text-[var(--eb-text-muted)] mb-3">
                        Compteur actuel — date : <strong>{cfg.DerniereDate || '—'}</strong>,
                        dernier numéro : <strong>{cfg.DernierNumero ?? '—'}</strong>
                        {cfg.DateMaj ? ` · maj ${formatDateFr(cfg.DateMaj)}` : ''}
                    </p>
                )}

                {props.canEdit && (
                    <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={16} />} onClick={handleSave}>
                        Enregistrer
                    </Button>
                )}
            </Spin>
        </SettingCard>
    );
}

/* ------------------------------------------------------------------ */
/*  Domaine VOIX_APPEL — vitesse de l'annonce vocale (0.50 .. 2.00)   */
/* ------------------------------------------------------------------ */
function SectionVoixAppel(props: { authHeader: () => string; codeAbonne: string; filiales: any[]; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [regles, setRegles] = useState<any[]>([])
    const [regleEdit, setRegleEdit] = useState<any>(null)

    const [codeFiliale, setCodeFiliale] = useState<string>('')
    const [vitesse, setVitesse] = useState<number>(1)

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        setLoading(true)
        getParametres(props.authHeader, 'VOIX_APPEL', props.codeAbonne)
            .then((res: any) => {
                if (res.StatutCode == 200) setRegles(res.Result || [])
                else { setRegles([]); alert.warning(res.Message) }
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setLoading(false));
    }

    function handleEdit(regle: any) {
        setRegleEdit(regle)
        setCodeFiliale(regle.CodeFiliale ?? '')
        setVitesse(regle.Vitesse ?? 1)
    }

    function handleDelete(regle: any) {
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('VOIX_APPEL', 'DELETE', props.codeAbonne),
            CodeFiliale: regle.CodeFiliale ?? null,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setRegles(res.Result || []); setRegleEdit(null) }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    function handleSave() {
        const v = Number(vitesse)
        if (!v || v < 0.5 || v > 2) {
            alert.warning('La vitesse doit être entre 0.50 (lent) et 2.00 (rapide) — 1.00 = normal.')
            return
        }
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('VOIX_APPEL', 'UPSERT', props.codeAbonne),
            CodeFiliale: codeFiliale || null,
            Vitesse: v,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setRegles(res.Result || []); setRegleEdit(null) }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    return (
        <SettingCard title="Voix d'appel" icon={<Volume2 size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-3 leading-relaxed">
                    Vitesse de l'annonce vocale sur l'écran d'appel : 0.50 = lent, 1.00 = normal (défaut), 2.00 = rapide.
                </p>

                <div className="mb-3 overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Filiale</th>
                                <th className="px-3 py-2 font-semibold">Vitesse</th>
                                <th className="px-3 py-2 font-semibold">Dernière maj</th>
                                {props.canEdit && <th className="px-3 py-2 font-semibold w-24">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {regles.length === 0 && (
                                <tr>
                                    <td colSpan={props.canEdit ? 4 : 3} className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">
                                        Aucune règle configurée — vitesse normale par défaut
                                    </td>
                                </tr>
                            )}
                            {regles.map((r: any, i: number) => (
                                <tr key={r.Id ?? i} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2">{r.CodeFiliale || 'Règle globale'}</td>
                                    <td className="px-3 py-2">
                                        <Badge color={r.Vitesse == 1 ? 'gray' : r.Vitesse < 1 ? 'amber' : 'green'}>
                                            ×{Number(r.Vitesse).toFixed(2)}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-[var(--eb-text-muted)]">{formatDateFr(r.DateMaj)}</td>
                                    {props.canEdit && (
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={28} onClick={() => handleEdit(r)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton tooltip="Supprimer" color="danger" size={28} onClick={() => handleDelete(r)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {props.canEdit && (
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[220px] flex-1">
                            <Select
                                label="Filiale"
                                value={codeFiliale}
                                onChange={setCodeFiliale}
                                options={[
                                    { value: '', label: 'Règle globale' },
                                    ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                ]}
                            />
                        </div>
                        <div className="w-40">
                            <Input
                                label="Vitesse (0.50 - 2.00)"
                                type="number"
                                min={0.5}
                                max={2}
                                step={0.05}
                                value={vitesse}
                                onChange={(e: any) => setVitesse(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-1.5 pb-1">
                            {[0.75, 1, 1.5].map(v => (
                                <button
                                    key={v}
                                    type='button'
                                    onClick={() => setVitesse(v)}
                                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors
                                        ${vitesse == v
                                            ? 'bg-[var(--eb-primary)] text-white border-[var(--eb-primary)]'
                                            : 'border-[var(--eb-border)] text-[var(--eb-text-muted)] hover:text-[var(--eb-text)]'}`}
                                >
                                    {v === 0.75 ? 'Lent' : v === 1 ? 'Normal' : 'Rapide'}
                                </button>
                            ))}
                        </div>
                        <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={16} />} onClick={handleSave}>
                            {regleEdit ? 'Mettre à jour' : 'Enregistrer'}
                        </Button>
                    </div>
                )}
            </Spin>
        </SettingCard>
    );
}

/* ------------------------------------------------------------------ */
/*  Domaine RESET_TICKETS — réinitialisation programmée / immédiate    */
/* ------------------------------------------------------------------ */
function SectionResetTickets(props: { authHeader: () => string; codeAbonne: string; filiales: any[]; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [executing, setExecuting] = useState(false)
    const [regles, setRegles] = useState<any[]>([])
    const [regleEdit, setRegleEdit] = useState<any>(null)

    const [codeFiliale, setCodeFiliale] = useState<string>('')
    const [heureReset, setHeureReset] = useState<string>('')
    const [dateReset, setDateReset] = useState<string>('')
    const [isActif, setIsActif] = useState<boolean>(true)

    // Confirmation avant exécution immédiate (action irréversible)
    const [confirmExec, setConfirmExec] = useState<null | { filiale: string }>(null)

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        setLoading(true)
        getParametres(props.authHeader, 'RESET_TICKETS', props.codeAbonne)
            .then((res: any) => {
                if (res.StatutCode == 200) setRegles(res.Result || [])
                else { setRegles([]); alert.warning(res.Message) }
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setLoading(false));
    }

    function handleEdit(regle: any) {
        setRegleEdit(regle)
        setCodeFiliale(regle.CodeFiliale ?? '')
        setHeureReset(regle.HeureReset ? String(regle.HeureReset).substring(0, 5) : '')
        setDateReset(regle.DateReset ? String(regle.DateReset).substring(0, 16) : '')
        setIsActif(regle.IsActif !== false)
    }

    function handleDelete(regle: any) {
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('RESET_TICKETS', 'DELETE', props.codeAbonne),
            CodeFiliale: regle.CodeFiliale ?? null,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setRegles(res.Result || []); setRegleEdit(null) }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    function handleSave() {
        if (!heureReset && !dateReset) {
            alert.warning('Renseignez une heure quotidienne et/ou une date ponctuelle.')
            return
        }
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('RESET_TICKETS', 'UPSERT', props.codeAbonne),
            CodeFiliale: codeFiliale || null,
            HeureReset: heureReset || null,
            DateReset: dateReset ? dateReset + ':00' : null,
            IsActif: isActif,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setRegles(res.Result || []); setRegleEdit(null) }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    function handleExecuter() {
        if (!confirmExec) return
        setExecuting(true)
        postParametres(props.authHeader, {
            ...basePayload('RESET_TICKETS', 'EXECUTER', props.codeAbonne),
            CodeFiliale: confirmExec.filiale || null,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) {
                    const nb = res.Result?.[0]?.NbTicketsExpires
                    alert.success(nb != null ? `${res.Message} — ${nb} ticket(s) expiré(s)` : res.Message)
                    LoadData()
                }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => { setExecuting(false); setConfirmExec(null) });
    }

    return (
        <SettingCard title="Réinitialisation des tickets" icon={<RotateCcw size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-3 leading-relaxed">
                    Les tickets non clôturés passent « Expiré » et la numérotation repart à 1 —
                    par défaut chaque jour à minuit.
                </p>

                <div className="mb-3 overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Filiale</th>
                                <th className="px-3 py-2 font-semibold">Heure quotidienne</th>
                                <th className="px-3 py-2 font-semibold">Date ponctuelle</th>
                                <th className="px-3 py-2 font-semibold">Statut</th>
                                <th className="px-3 py-2 font-semibold">Dernier reset</th>
                                {props.canEdit && <th className="px-3 py-2 font-semibold w-24">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {regles.length === 0 && (
                                <tr>
                                    <td colSpan={props.canEdit ? 6 : 5} className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">
                                        Aucune règle configurée — reset à minuit par défaut
                                    </td>
                                </tr>
                            )}
                            {regles.map((r: any, i: number) => (
                                <tr key={r.Id ?? i} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2">{r.CodeFiliale || 'Règle globale'}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.HeureReset ? String(r.HeureReset).substring(0, 5) : '—'}</td>
                                    <td className="px-3 py-2 text-xs">{r.DateReset ? formatDateFr(r.DateReset) : '—'}</td>
                                    <td className="px-3 py-2">
                                        <Badge color={r.IsActif !== false ? 'green' : 'gray'}>
                                            {r.IsActif !== false ? 'Actif' : 'Suspendu'}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-[var(--eb-text-muted)]">{r.DernierReset ? formatDateFr(r.DernierReset) : '—'}</td>
                                    {props.canEdit && (
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={28} onClick={() => handleEdit(r)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton tooltip="Supprimer" color="danger" size={28} onClick={() => handleDelete(r)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {props.canEdit && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <Select
                                label="Filiale"
                                value={codeFiliale}
                                onChange={setCodeFiliale}
                                options={[
                                    { value: '', label: 'Règle globale' },
                                    ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                ]}
                            />
                            <div className="flex items-center gap-2 pb-2">
                                <Switch checked={isActif} onChange={setIsActif} />
                                <span className="text-sm text-[var(--eb-text)]">Règle active</span>
                            </div>
                            <Input
                                label="Heure quotidienne"
                                type="time"
                                value={heureReset}
                                onChange={(e: any) => setHeureReset(e.target.value)}
                            />
                            <Input
                                label="Ou date ponctuelle (une fois)"
                                type="datetime-local"
                                value={dateReset}
                                onChange={(e: any) => setDateReset(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={16} />} onClick={handleSave}>
                                {regleEdit ? 'Mettre à jour' : 'Enregistrer'}
                            </Button>
                            <Button
                                variant='danger'
                                size='sm'
                                disabled={executing}
                                startIcon={<RotateCcw size={16} />}
                                onClick={() => setConfirmExec({ filiale: codeFiliale })}
                            >
                                Réinitialiser maintenant
                            </Button>
                        </div>
                    </>
                )}

                <Modal
                    open={!!confirmExec}
                    onClose={() => setConfirmExec(null)}
                    title="Réinitialisation immédiate"
                    footer={
                        <>
                            <Button variant='ghost' size='sm' onClick={() => setConfirmExec(null)}>Annuler</Button>
                            <Button variant='danger' size='sm' loading={executing} startIcon={<RotateCcw size={16} />} onClick={handleExecuter}>
                                Réinitialiser
                            </Button>
                        </>
                    }
                >
                    <p className="text-sm text-[var(--eb-text)]">
                        Tous les tickets non clôturés
                        {confirmExec?.filiale ? <> de la filiale <b>{confirmExec.filiale}</b></> : <> de <b>toute l'entité</b></>}
                        {' '}passeront « Expiré » et la numérotation repartira à 1.
                    </p>
                    <p className="text-xs text-[var(--eb-text-muted)] mt-2">
                        Action irréversible — l'opération sera tracée à votre nom.
                    </p>
                </Modal>
            </Spin>
        </SettingCard>
    );
}

/* ------------------------------------------------------------------ */
/*  Domaine MODE_ANNONCE — qui prononce l'appel                        */
/* ------------------------------------------------------------------ */
const MODES_ANNONCE = [
    { value: 'CENTRALISE', label: 'Centralisé', desc: 'L\'écran d\'appel parle, le guichet est muet' },
    { value: 'LOCAL', label: 'Local', desc: 'Le poste du guichet parle, l\'écran affiche seulement' },
    { value: 'LES_DEUX', label: 'Les deux', desc: 'L\'écran et le guichet parlent' },
];

function labelMode(v: string) {
    return MODES_ANNONCE.find(m => m.value === v)?.label || v
}

function SectionModeAnnonce(props: { authHeader: () => string; codeAbonne: string; filiales: any[]; services: any[]; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [regles, setRegles] = useState<any[]>([])
    const [regleEdit, setRegleEdit] = useState<any>(null)

    const [codeFiliale, setCodeFiliale] = useState<string>('')
    const [codeS, setCodeS] = useState<string>('')
    const [mode, setMode] = useState<string>('CENTRALISE')

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const servicesFiltres = props.services.filter((s: any) => !codeFiliale || s.CodeFiliale == codeFiliale)

    function LoadData() {
        setLoading(true)
        getParametres(props.authHeader, 'MODE_ANNONCE', props.codeAbonne)
            .then((res: any) => {
                if (res.StatutCode == 200) setRegles(res.Result || [])
                else { setRegles([]); alert.warning(res.Message) }
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setLoading(false));
    }

    function handleEdit(regle: any) {
        setRegleEdit(regle)
        setCodeFiliale(regle.CodeFiliale ?? '')
        setCodeS(regle.CodeS ?? '')
        setMode(regle.ModeAnnonce ?? 'CENTRALISE')
    }

    function handleDelete(regle: any) {
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('MODE_ANNONCE', 'DELETE', props.codeAbonne),
            CodeFiliale: regle.CodeFiliale ?? null,
            CodeS: regle.CodeS ?? null,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setRegles(res.Result || []); setRegleEdit(null) }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    function handleSave() {
        setSaving(true)
        postParametres(props.authHeader, {
            ...basePayload('MODE_ANNONCE', 'UPSERT', props.codeAbonne),
            CodeFiliale: codeFiliale || null,
            CodeS: codeS || null,
            ModeAnnonce: mode,
        })
            .then((res: any) => {
                if (res.StatutCode == 200) { alert.success(res.Message); setRegles(res.Result || []); setRegleEdit(null) }
                else alert.warning(res.Message)
            })
            .catch((error: any) => { alert.error('Error', error); console.log(error) })
            .finally(() => setSaving(false));
    }

    return (
        <SettingCard title="Mode d'annonce" icon={<Megaphone size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-3 leading-relaxed">
                    Qui prononce l'appel du client : l'écran (centralisé, par défaut), le poste du
                    guichet (local), ou les deux. Appliqué dès l'appel suivant.
                </p>

                <div className="mb-3 overflow-x-auto rounded-lg border border-[var(--eb-border)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[var(--eb-bg)] text-left text-xs text-[var(--eb-text-muted)]">
                                <th className="px-3 py-2 font-semibold">Filiale</th>
                                <th className="px-3 py-2 font-semibold">Service</th>
                                <th className="px-3 py-2 font-semibold">Mode</th>
                                <th className="px-3 py-2 font-semibold">Dernière maj</th>
                                {props.canEdit && <th className="px-3 py-2 font-semibold w-24">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {regles.length === 0 && (
                                <tr>
                                    <td colSpan={props.canEdit ? 5 : 4} className="px-3 py-4 text-center text-xs text-[var(--eb-text-muted)]">
                                        Aucune règle configurée — mode centralisé par défaut
                                    </td>
                                </tr>
                            )}
                            {regles.map((r: any, i: number) => (
                                <tr key={r.Id ?? i} className="border-t border-[var(--eb-border)]">
                                    <td className="px-3 py-2">{r.CodeFiliale || <span className="text-[var(--eb-text-muted)]">Globale</span>}</td>
                                    <td className="px-3 py-2">{r.CodeS || <span className="text-[var(--eb-text-muted)]">Tous</span>}</td>
                                    <td className="px-3 py-2">
                                        <Badge color={r.ModeAnnonce === 'CENTRALISE' ? 'gray' : 'blue'}>
                                            {labelMode(r.ModeAnnonce)}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-[var(--eb-text-muted)]">{formatDateFr(r.DateMaj)}</td>
                                    {props.canEdit && (
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <IconButton tooltip="Modifier" color="primary" size={28} onClick={() => handleEdit(r)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton tooltip="Supprimer" color="danger" size={28} onClick={() => handleDelete(r)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {props.canEdit && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <Select
                                label="Filiale"
                                value={codeFiliale}
                                onChange={(v: string) => { setCodeFiliale(v); setCodeS(''); }}
                                options={[
                                    { value: '', label: 'Règle globale' },
                                    ...props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
                                ]}
                            />
                            <Select
                                label="Service"
                                value={codeS}
                                onChange={setCodeS}
                                options={[
                                    { value: '', label: 'Tous les services' },
                                    ...servicesFiltres.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))
                                ]}
                            />
                        </div>

                        {/* Choix du mode en cartes radio */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                            {MODES_ANNONCE.map(m => (
                                <button
                                    key={m.value}
                                    type='button'
                                    onClick={() => setMode(m.value)}
                                    className={`text-left px-3 py-2.5 rounded-lg border transition-colors
                                        ${mode === m.value
                                            ? 'border-[var(--eb-primary)] bg-[var(--eb-primary-soft)]/50'
                                            : 'border-[var(--eb-border)] hover:border-[var(--eb-text-muted)]'}`}
                                >
                                    <span className={`block text-sm font-semibold ${mode === m.value ? 'text-[var(--eb-primary)]' : 'text-[var(--eb-text)]'}`}>
                                        {m.label}
                                    </span>
                                    <span className="block text-xs text-[var(--eb-text-muted)] mt-0.5">{m.desc}</span>
                                </button>
                            ))}
                        </div>

                        <Button variant='secondary' size='sm' loading={saving} startIcon={<Save size={16} />} onClick={handleSave}>
                            {regleEdit ? 'Mettre à jour' : 'Enregistrer'}
                        </Button>
                    </>
                )}
            </Spin>
        </SettingCard>
    );
}

/* ------------------------------------------------------------------ */
/*  Écran d'affichage — textes, logo, couleurs + messages vocaux      */
/*  (ex-page « Configuration texte & message », fusionnée ici)        */
/* ------------------------------------------------------------------ */
function SectionEcranLive(props: { authHeader: () => string; user: User; canEdit: boolean }) {

    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [savingTexte, setSavingTexte] = useState(false)
    const [savingMessage, setSavingMessage] = useState(false)

    const [text1, setTexte1] = useState("")
    const [text2, setTexte2] = useState("")
    const [sousText1, setSousTexte1] = useState("")
    const [sousText2, setSousTexte2] = useState("")
    const [sousTextCouleur, setSousTexteCouleur] = useState("")
    const [sousTextLogo, setSousTexteLogo] = useState("")

    const [msgFr, setMsgFr] = useState("")
    const [msgEn, setMsgEn] = useState("")
    const [sousMsgFr, setSousMsgFr] = useState("")
    const [sousMsgEn, setSousMsgEn] = useState("")
    const [previewImageDefault, setPreviewImageDefault] = useState("")

    const [color1, setColor1] = useState("#000000")
    const [color2, setColor2] = useState("#000000")

    const [fileList, setFileList] = useState<UploadedFile[]>([])

    useEffect(() => {
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function LoadData() {
        const data = { CodeAbonne: props.user.CodeAbonne, CreatedBy: props.user.CodeU }
        setLoading(true)
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.AllScreem + Crypter.encrypt(JSON.stringify(data)),
            headers: {
                'Accept': 'text/plain',
                'Authorization': props.authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    res.Result.forEach((ls: any) => {
                        if (ls.CodeParam == "LT1") {
                            setTexte1(ls.Choice)
                            setSousTexte1(ls.Libelle)
                        }
                        if (ls.CodeParam == "LT2") {
                            setTexte2(ls.Choice)
                            setSousTexte2(ls.Libelle)
                        }
                        if (ls.CodeParam == "MFR") {
                            setMsgFr(ls.Choice)
                            setSousMsgFr(ls.Libelle)
                        }
                        if (ls.CodeParam == "MEN") {
                            setMsgEn(ls.Choice)
                            setSousMsgEn(ls.Libelle)
                        }
                        if (ls.CodeParam == "LOGO") {
                            setPreviewImageDefault(ls.Choice);
                            setSousTexteLogo(ls.Libelle)
                        }
                        if (ls.CodeParam == "COLORS") {
                            if (ls.Choice != null) {
                                setColor1(ls.Choice.split("|")[0])
                                setColor2(ls.Choice.split("|")[1])
                            }
                            setSousTexteCouleur(ls.Libelle)
                        }
                    })
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                alert.error('Error', error)
                console.log(error);
            })
            .finally(() => setLoading(false));
    }

    function testVoice() {
        speech(msgFr.replaceAll("&", 'E10').replaceAll("@", "2") + "|" + msgEn.replaceAll("&", 'E10').replaceAll("@", "2"), 1)
    }

    function SaveTexte() {
        if (!(fileList.length > 0 || previewImageDefault)) {
            alert.warning("Veillez charger le logo de l'écran.")
            return
        }
        if (!(text1.length > 1 && text2.length > 1)) {
            alert.warning("Veillez renseigner les deux champs texte 1 et texte 2.")
            return
        }
        const fichier = (fileList.length > 0) ? fileList[0].thumbUrl : previewImageDefault
        const data = { LT1: text1, LT2: text2, CodeAbonne: props.user.CodeAbonne, CreatedBy: props.user.CodeU, Logo: fichier, Colors: color1 + "|" + color2 }
        setSavingTexte(true)

        const config = {
            method: 'put',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.UpdateTextLiveScreem,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': props.authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(data))
        };

        axios.request(config)
            .then((response) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    LoadData()
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error) => {
                alert.error(error.message)
            })
            .finally(() => setSavingTexte(false));
    }

    function SaveMessage() {
        if (!(msgFr.length > 1 && msgEn.length > 1)) {
            alert.warning("Veillez renseigner les messages d'abord.", "Tout en respectant le format exgigé !")
            return
        }
        const data = { LT1: msgFr, LT2: msgEn, CodeAbonne: props.user.CodeAbonne, CreatedBy: props.user.CodeU }
        setSavingMessage(true)

        const config = {
            method: 'put',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.UpdatetTexVoiceLiveScreem,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': props.authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(data))
        };

        axios.request(config)
            .then((response) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error) => {
                alert.error(error.message)
            })
            .finally(() => setSavingMessage(false));
    }

    return (
        <SettingCard title="Écran d'affichage — textes, logo, couleurs & voix" icon={<Monitor size={18} />}>
            <Spin spinning={loading}>
                <p className="text-xs text-[var(--eb-text-muted)] mb-4 leading-relaxed">
                    Contenu affiché sur les écrans (LiveScreem) et messages prononcés lors de l'appel du client.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
                    {/* Colonne réglages */}
                    <div className="space-y-5">
                        {/* Logo */}
                        <div>
                            <span className="block text-sm font-semibold text-[var(--eb-text)] mb-2">Logo de l'écran</span>
                            <div className="flex flex-wrap items-start gap-4">
                                {props.canEdit && (
                                    <div className="min-w-[220px]">
                                        <FileUpload
                                            files={fileList}
                                            onChange={setFileList}
                                            accept="image/png, image/jpeg, image/jpg, image/svg"
                                            maxCount={1}
                                        />
                                    </div>
                                )}
                                <div className="flex flex-col gap-2">
                                    <span className="text-xs text-[var(--eb-text-muted)]" title={sousTextLogo}>Logo actuel</span>
                                    {previewImageDefault && (
                                        <ImagePreview width={120} src={previewImageDefault} alt="Logo actuel" style={{ borderRadius: 8 }} />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Textes */}
                        <div>
                            <span className="block text-sm font-semibold text-[var(--eb-text)] mb-2">Textes affichés</span>
                            <div className="space-y-3">
                                <Input value={text1} onChange={(e) => { setTexte1(e.target.value) }} label={"Texte 1 : " + sousText1} disabled={!props.canEdit} />
                                <Textarea value={text2} onChange={(e) => { setTexte2(e.target.value) }} rows={2} label={"Texte 2 : " + sousText2} disabled={!props.canEdit} />
                            </div>
                        </div>

                        {/* Couleurs */}
                        <div>
                            <span className="block text-sm font-semibold text-[var(--eb-text)] mb-2">Couleurs de fond</span>
                            <div className="eb-color-palette">
                                <div className="eb-color-palette-fields">
                                    <ColorField label="Haut" value={color1} onChange={setColor1} />
                                    <ColorField label="Bas" value={color2} onChange={setColor2} />
                                </div>
                                <div className="eb-color-palette-preview" style={{ background: `linear-gradient(${color1}, ${color2})` }}>
                                    <span className="eb-color-palette-preview-label">{sousTextCouleur}</span>
                                </div>
                            </div>
                        </div>

                        {props.canEdit && (
                            <div className="flex">
                                <Button variant='secondary' size='sm' loading={savingTexte} startIcon={<Save size={16} />} onClick={SaveTexte}>
                                    Enregistrer l'affichage
                                </Button>
                            </div>
                        )}

                        <div className="border-t border-[var(--eb-border)]" />

                        {/* Messages vocaux */}
                        <div>
                            <span className="block text-sm font-semibold text-[var(--eb-text)] mb-1">Messages d'appel vocal</span>
                            <p className="text-xs text-[var(--eb-text-muted)] mb-3">Messages prononcés lors de l'appel du client (FR / EN).</p>
                            <div className="space-y-3">
                                <Input value={msgFr} onChange={(e) => { setMsgFr(e.target.value) }} label={sousMsgFr} disabled={!props.canEdit} />
                                <Input value={msgEn} onChange={(e) => { setMsgEn(e.target.value) }} label={sousMsgEn} disabled={!props.canEdit} />
                            </div>
                            <div className="mt-3">
                                <p className="text-[13px] text-red-500">
                                    <u>NB</u> : Mettez le symbole & pour le numéro du client et le symbole @ pour le numéro de la box
                                </p>
                                <p className="text-xs text-[var(--eb-text-muted)]">
                                    Pour le test : ( & = E10 et @ = 2 )
                                </p>
                            </div>
                        </div>

                        {props.canEdit && (
                            <div className="flex flex-wrap gap-2">
                                <Button variant='outline' size='sm' startIcon={<Mic size={16} />} onClick={testVoice}>
                                    Tester
                                </Button>
                                <Button variant='outline' size='sm' loading={savingMessage} onClick={SaveMessage}
                                    className="!text-emerald-600 !border-emerald-500/60 hover:!bg-emerald-50" startIcon={<Save size={16} />}>
                                    Enregistrer le message
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Colonne aperçu */}
                    <div>
                        <LivePreview
                            text1={text1}
                            text2={text2}
                            logo={previewImageDefault}
                            color1={color1}
                            color2={color2}
                            subtitle={sousTextCouleur}
                        />
                    </div>
                </div>
            </Spin>
        </SettingCard>
    );
}
